import React, { MutableRefObject, useEffect, useRef } from 'react';
import { CTAConfig } from '@oneaudi/vtp-configuration-service';
import { useI18n } from '@oneaudi/i18n-context';

import { TextButton, Popover } from '@oneaudi/unified-web-components';
import { SYS_BREAKPOINT_LG } from '@oneaudi/unified-web-common';

import styled from 'styled-components';

import {
  CompleteVehicleEntry,
  DynamicFinancingValues,
  FinanceCheckoutPopoverButton,
} from '@oneaudi/vtp-shared';

import { RenderedButton } from './RenderedButton';
import { ButtonWrapper, InformationContainer } from '../styles/ConversionBar.styles';
import { FinanceInfoConversionBar } from './FinanceInfoConversionBar';

interface InformationWrapperProps {
  setPopover: (status: boolean) => void;
  popOverStatus: boolean;
  vehicleRaw: CompleteVehicleEntry;
  scContextId: string;
  buttonsState: CTAConfig[];
  isColumn: boolean;
  isDesktop: boolean;
  conversionBarRef: MutableRefObject<null>;
  dynamicFinancingValues?: DynamicFinancingValues;
  updateDynamicFinancingValues?: (values?: DynamicFinancingValues) => void;
}

export const InformationWrapper: React.FC<InformationWrapperProps> = ({
  setPopover,
  popOverStatus,
  vehicleRaw,
  scContextId,
  buttonsState,
  isColumn,
  isDesktop,
  conversionBarRef,
  dynamicFinancingValues,
  updateDynamicFinancingValues,
}) => {
  const vehicleBasic = vehicleRaw.basic;
  const conversionbarButtonWrapper = useRef<HTMLDivElement>(null);
  const [wrapperReady, setWrapperReady] = React.useState(false);
  const [fsCheckoutProps, setFSCheckoutProps] = React.useState<Record<string, any> | null>(null);
  const [showCheckoutDialog, setShowCheckoutDialog] = React.useState(false);

  enum DeviceType {
    Mobile = 'Mobile',
    Tablet = 'Tablet',
    Desktop = 'Desktop',
  }
  const [deviceType, setDeviceType] = React.useState<DeviceType>(DeviceType.Mobile);
  const checkDeviceType = () => {
    if (window.matchMedia('(max-width: 767px)').matches) setDeviceType(DeviceType.Mobile);
    else if (window.matchMedia('(max-width: 1023px)').matches) setDeviceType(DeviceType.Tablet);
    else setDeviceType(DeviceType.Desktop);
  };

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      checkDeviceType();
      window.addEventListener('resize', checkDeviceType);
      return () => {
        window.removeEventListener('resize', checkDeviceType);
      };
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (conversionbarButtonWrapper.current) {
      setWrapperReady(true);
    }
  }, [conversionbarButtonWrapper.current]);

  const onClickToOpenFSPopup = (propsFromFSCheckout: any) => {
    setFSCheckoutProps(propsFromFSCheckout);
    setShowCheckoutDialog(true);
  };

  const onClickToCloseFSPopup = () => {
    setShowCheckoutDialog(false);
  };

  const renderedButtonArgs = {
    vehicle: vehicleBasic,
    scContextId,
    buttonsState,
    isColumn,
    isDesktop,
    dynamicFinancingValues,
    onClickToOpenFSPopup: !buttonsState.some((item) => item.type === 'finance-checkout')
      ? undefined
      : onClickToOpenFSPopup,
  };

  const conversionBar = {
    iamInterestedBtn: useI18n({
      defaultMessage: 'I am interested',
      id: 'nemo.ui.sc.details.conversionbar.iamInterestedbtn',
    }),
  };

  return (
    <InformationContainer data-testid="conversion-information">
      <FinanceInfoConversionBar
        vehicleRaw={vehicleRaw}
        vehicleBasic={vehicleBasic}
        isColumn={isColumn}
        dynamicFinancingValues={dynamicFinancingValues}
        updateDynamicFinancingValues={updateDynamicFinancingValues}
      />
      {buttonsState.some((item) => item.type === 'finance-checkout') &&
        showCheckoutDialog &&
        fsCheckoutProps && (
          <FinanceCheckoutPopoverButton
            {...fsCheckoutProps}
            buttonInfo={fsCheckoutProps.buttonInfo}
            vehicle={vehicleBasic}
            trackButtonClick={fsCheckoutProps.trackButtonClick}
            variant={fsCheckoutProps.variant}
            onClickToCloseFSPopup={
              !buttonsState.some((item) => item.type === 'finance-checkout')
                ? undefined
                : onClickToCloseFSPopup
            }
          />
        )}
      <ButtonWrapper data-testid="conversionbar_buttonWrapper" ref={conversionbarButtonWrapper}>
        {buttonsState && (buttonsState.length === 1 || buttonsState.length === 2) && (
          <RenderedButton {...renderedButtonArgs} />
        )}
        {buttonsState && !isColumn && wrapperReady && conversionbarButtonWrapper && (
          <Popover
            id="interestedPopover"
            zIndex={10}
            placement="top"
            alignment="center"
            open={popOverStatus}
            withTip={false}
            onClose={() => setPopover(false)}
            content={<RenderedButton {...renderedButtonArgs} />}
            targetRef={deviceType === DeviceType.Mobile ? conversionBarRef : undefined}
            triggerElement={
              <ButtonPopoverTriggerContainer>
                <TextButton
                  variant="primary"
                  aria-label="I am interested"
                  onClick={() => setPopover(true)}
                  type="button"
                  isFullWidth={!isDesktop}
                  size="md"
                >
                  {conversionBar.iamInterestedBtn}
                </TextButton>
              </ButtonPopoverTriggerContainer>
            }
          />
        )}
      </ButtonWrapper>
    </InformationContainer>
  );
};

// The Popover has a space to the trigger, but it should have a distance to the ConversionBar, therefore increasing the
// calculated size of the button without moving it
const ButtonPopoverTriggerContainer = styled.div`
  margin-top: -10px;
  padding-top: 10px;

  @media (min-width: ${SYS_BREAKPOINT_LG}) {
    margin-top: -20px;
    padding-top: 20px;
  }
`;
